import {
  AloftDate,
  AloftDateJSON,
  CompsListingsSearchCriteria,
  MLSListing,
  PropertyData,
  RecommendedCompsStatistics,
  SubjectData,
  isDefined,
  noop,
} from '@aloft/toolkit-shared';
import { Block, Flex, Modal } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { useDisclosure } from '@aloft/toolkit-sui/chakra';
import { SortingState } from '@tanstack/react-table';

import { SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useAutofillProperty } from '../../hooks/useAutofillProperty.ts';
import { dismissAlertToasts } from '../../hooks/useToaster/dismissAllToasts.ts';
import { Tkv2ReferenceViewDrawer } from '../../modules/referenceView/Tkv2ReferenceViewDrawer.tsx';
import { removeComps } from '../../redux/features/actions/removeComp.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../redux/features/referenceBarUISlice.ts';
import { featuresSelectors } from '../../redux/features/selectors/featuresSelectors.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { LoadingScreenSamFull } from '../LoadingScreen/LoadingScreenSamFull.tsx';
import { Tkv2ReferenceBar } from '../ReferenceBar/Tkv2ReferenceBar.tsx';
import { RecCompSelectionProvider } from './RecCompSelectionContext.tsx';
import { RecommendedCompsModalHeader } from './RecommendedCompsModalHeader.tsx';
import { useRecommendedCompsFilterRoute } from './hooks/useRecommendedCompsFilterRoute.ts';
import { useRecommendedCompsInternalState } from './hooks/useRecommendedCompsInternalState.ts';
import { useRecommendedProperties } from './hooks/useRecommendedProperties.ts';
import { useSimilarityScoreWeightingModal } from './hooks/useSimilarityScoreWeightingModal.tsx';
import { useSimilarityScoreWeights } from './hooks/useSimilarityScoreWeights.ts';
import { ColumnSettingsModal } from './internal/ColumnSettingsModal.tsx';
import { FilterDrawer } from './internal/FilterDrawer/FilterDrawer.tsx';
import { dismissDefaultFiltersInfo } from './internal/FilterDrawer/defaultFiltersInfoToastStorage.ts';
import { RecommendedCompSelection } from './internal/RecommendedCompSelection.tsx';
import { QualityTierBreakdown } from './internal/qualityTier/QualityTierBreakdown.tsx';
import {
  CompSelectionProperty,
  CompSelectionState,
  FiltersMessage,
  QualityTierBreakdownData,
  schCompSearch,
} from './types.ts';
import { getSelectedIDs } from './utils/compSelectionState.ts';
import { handleAddClickForCompCreation } from './utils/handleAddClickForCompCreation.ts';
import { handleAddClickZillow } from './utils/handleAddClickZillow.ts';
import { loadExistingSearchCriteria } from './utils/loadExistingSearchCriteria.ts';

const { selectShouldShowRecommendedCompsFiltering, selectShouldAllowNewSimilarityScore } =
  featuresSelectors;

type RecommendedCompsModalProps = {
  createComps: (props: PropertyData[]) => void;
  currentComps: Record<string, PropertyData>;
  compSeq: string[];
  orderID: string;
  isModalOpen: boolean;
  onModalClose: () => void;
  subject: SubjectData;
  effectiveDate: AloftDateJSON;
};

export const RecommendCompsModal = ({
  createComps,
  currentComps,
  compSeq,
  orderID,
  isModalOpen,
  onModalClose,
  subject,
  effectiveDate,
}: RecommendedCompsModalProps) => {
  const dispatch = useDispatch();
  const { isLoading, data, getProperties, isError } = useRecommendedProperties({ orderID });
  const order = useAppSelector(selectOrder);
  const { hasArvOption } = order;
  const autofillProperty = useAutofillProperty();
  const { SimilarityScoreWeightingModal, openSimilarityScoreWeightingModal } =
    useSimilarityScoreWeightingModal();

  const [isAddCompsLoading, setIsAddCompsLoading] = useState(false);
  const [newFiltersApplied, setNewFiltersApplied] = useState(false);
  const [filtersMessage, setFiltersMessage] = useState<FiltersMessage | undefined>(undefined);
  const [allowApplyNewFilters, setAllowApplyNewFilters] = useState(false);

  const ref: React.RefObject<{
    downloadCSV: () => void;
    getQualityTiersBreakdown: () => QualityTierBreakdownData;
    getRowsWithUADRatings: () => CompSelectionProperty[];
  } | null> = useRef(null);

  // Keep track of the comps that the user selected, so we can add them when we are ready
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<CompSelectionState>({});
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const previousDataRef = useRef<typeof data>();
  const hasInitializedFormFromDataRef = useRef(false);
  const [qualityTierBreakdownData, setQualityTierBreakdownData] =
    useState<QualityTierBreakdownData>();

  let existingSearchCriteria: CompsListingsSearchCriteria | undefined;

  if (data?.body.output?.searchCriteria) existingSearchCriteria = data.body.output.searchCriteria;

  let statistics: RecommendedCompsStatistics | undefined;
  if (
    (data?.source === 'constellation1' || data?.source === 'abode') &&
    data?.body?.status === 'found'
  ) {
    statistics = data.body?.output?.statistics;
  }

  const featureAllowRecommendedCompsFiltering = useAppSelector(
    selectShouldShowRecommendedCompsFiltering,
  );

  const allowRecommendedCompsFiltering =
    featureAllowRecommendedCompsFiltering || order.isInspectifyOrder;

  const allowNewSimilarityScore = useAppSelector(selectShouldAllowNewSimilarityScore);

  // Pre-fetch similarity score weights in the background so the modal opens instantly
  const { weights: similarityScoreWeights } = useSimilarityScoreWeights(orderID);

  const { form, showNoRecommendations, setShowNoRecommendations, getDefaultFormValues } =
    useRecommendedCompsInternalState(subject, AloftDate.schema.parse(effectiveDate));

  const currentCompsSet = useMemo(
    (): Record<string, string> =>
      Object.entries(currentComps)
        .map(([compId, comp]) => {
          const toolkitId = comp.readonlyData.backupListingData?.listing?.ToolkitListingID;
          return toolkitId ? [toolkitId as string, compId] : null;
        })
        .filter((entry): entry is [string, string] => entry !== null)
        .reduce<Record<string, string>>((acc, [toolkitId, compId]) => {
          acc[toolkitId] = compId;
          return acc;
        }, {}),
    [currentComps],
  );

  useEffect(() => {
    if (!isModalOpen) return;

    dismissAlertToasts();
    dispatch(setSelectedDataSheet('mls-map'));
    dispatch(setReferenceViewOpen(true));
  }, [isModalOpen]);

  const { setValue } = form;
  useEffect(() => {
    if (!isModalOpen) {
      hasInitializedFormFromDataRef.current = false;
      previousDataRef.current = undefined;
      return;
    }
    if (data && !isError && !hasInitializedFormFromDataRef.current) {
      hasInitializedFormFromDataRef.current = true;
      previousDataRef.current = data;

      const search = existingSearchCriteria
        ? loadExistingSearchCriteria(existingSearchCriteria, subject, getDefaultFormValues)
        : getDefaultFormValues();
      setValue('search', search);
      setValue('originalSearch', search);
    }
  }, [isModalOpen, data, isError, existingSearchCriteria, subject, getDefaultFormValues, setValue]);

  useEffect(() => {
    if (!data || isError) return;

    const currentCompsKeys = Object.keys(currentCompsSet || {});
    if (!hasInitializedSelection && currentCompsKeys.length > 0) {
      // Reset row selection to only include current comps
      // Only do this once when data first loads, not on every dependency change
      if (hasArvOption && order.isInspectifyOrder) {
        // Initialize with existing comps and their compType from PropertyData
        // Handle case where multiple comps might share the same listingID with different compTypes
        setRowSelection((prevSelection) => {
          const newSelection: CompSelectionState = { ...prevSelection };

          // First, initialize from currentCompsSet (mapping listingID -> compId)
          currentCompsKeys.forEach((listingId) => {
            const compId = currentCompsSet[listingId];
            const comp = compId ? currentComps[compId] : undefined;
            if (comp) {
              // Initialize or update selection for this listingID
              const existing = newSelection[listingId] || {};
              newSelection[listingId] = {
                aiv: existing.aiv || comp.compType === 'aiv',
                arv: existing.arv || comp.compType === 'arv',
              };
            }
          });

          // Also check all comps in case there are multiple comps with same listingID but different compTypes
          Object.values(currentComps).forEach((comp) => {
            const listingId =
              comp.readonlyData.backupListingData?.listing?.ToolkitListingID ||
              comp.readonlyData.backupListingData?.listing?.ListingId;
            if (listingId) {
              const existing = newSelection[listingId] || {};
              newSelection[listingId] = {
                aiv: existing.aiv || comp.compType === 'aiv',
                arv: existing.arv || comp.compType === 'arv',
              };
            }
          });

          return newSelection;
        });
      } else {
        // Old behavior: just initialize with listing IDs as keys
        // This applies to non-Inspectify orders and Inspectify orders without ARV option
        setRowSelection(() => {
          const newSelection: CompSelectionState = {};
          currentCompsKeys.forEach((listingId) => {
            newSelection[listingId] = {};
          });
          return newSelection;
        });
      }
      setHasInitializedSelection(true);
    }
  }, [
    data,
    isError,
    currentCompsSet,
    currentComps,
    hasInitializedSelection,
    order.isInspectifyOrder,
    hasArvOption,
  ]);

  // Subscribe to form changes to track when search values differ from originalSearch
  useEffect(() => {
    const subscribe = form.subscribe({
      formState: {
        values: true,
      },
      callback: (state) => {
        // Compare the UI search values with the version that was submitted to the backend
        const stateChangedFromOriginal =
          JSON.stringify(state.values.originalSearch) !== JSON.stringify(state.values.search);

        const { geoSearch } = state.values.search;
        const geoSearchValid =
          (geoSearch.t === 'polygon' && geoSearch.coords.length > 0) ||
          (geoSearch.t === 'radius' && geoSearch.radiusInMeters > 0);
        setAllowApplyNewFilters(stateChangedFromOriginal && geoSearchValid);
      },
    });

    return subscribe;
  }, [form]);

  const { isFilterDrawerOpen, onFilterDrawerOpen, onFilterDrawerClose } =
    useRecommendedCompsFilterRoute();

  const {
    isOpen: isQualityTierBreakdownOpen,
    onOpen: onQualityTierBreakdownOpen,
    onClose: onQualityTierBreakdownClose,
  } = useDisclosure();

  const {
    isOpen: isColumnSettingsOpen,
    onOpen: onColumnSettingsOpen,
    onClose: onColumnSettingsClose,
  } = useDisclosure();

  const handleFilterDrawerClose = () => {
    onFilterDrawerClose();
    form.setValue('search', form.getValues('originalSearch'));
  };

  const applyFilters = () => {
    const appliedSearch = schCompSearch.parse(form.getValues('search'));
    form.setValue('search', appliedSearch);
    form.setValue('originalSearch', appliedSearch);
    setShowNoRecommendations(false);
    getProperties({ search: appliedSearch });
    setNewFiltersApplied(true);
    setFiltersMessage(undefined);
    setAllowApplyNewFilters(false);
    dismissDefaultFiltersInfo(orderID);
  };

  useEffect(() => {
    if (isLoading) return;

    if (isError || (data && data.body.status === 'error')) {
      setFiltersMessage({
        type: 'error',
        message: 'Failed to load recommended comps. Please try again or update your filters.',
        color: 'alertError',
        icon: 'warning',
      });
    } else if (!newFiltersApplied) {
      setFiltersMessage(undefined);
    } else if (
      data &&
      (data.source === 'abode' || data.source === 'constellation1') &&
      data?.body.status === 'found' &&
      data?.body.output.resultsCount > 100
    ) {
      setFiltersMessage({
        type: 'warning',
        message: 'Too many results, consider narrowing your filters for better focus.',
        color: 'statusWarning',
        icon: 'warning',
      });
    } else if (data && data.body.status === 'not_found') {
      setFiltersMessage({
        type: 'warning',
        message: 'No comps found with the current filters. Try broadening your search criteria.',
        color: 'statusWarning',
        icon: 'warning',
      });
    } else if (newFiltersApplied && !allowApplyNewFilters) {
      setFiltersMessage({
        type: 'success',
        message: 'Filters applied!',
        color: 'alertSuccess',
        icon: 'check',
      });
    } else {
      setFiltersMessage(undefined);
    }
  }, [data, isLoading, newFiltersApplied, isError, allowApplyNewFilters]);

  const handleSetRowSelection = (newSet: SetStateAction<CompSelectionState>) => {
    const resolvedValue = typeof newSet === 'function' ? newSet(rowSelection) : newSet;
    Object.keys(resolvedValue).forEach((key) => {
      const selection = resolvedValue[key];
      if (selection && !selection?.listingData && data?.body.status === 'found') {
        const listing = data.body.output.results.find(
          (result) =>
            ('ToolkitListingID' in result && result.ToolkitListingID === key) ||
            ('ListingId' in result && result.ListingId === key),
        );
        if (listing) {
          selection.listingData = listing as MLSListing;
        }
      }
    });
    setRowSelection(resolvedValue);
  };

  const dismissModal = () => {
    setRowSelection({}); // reset row selection
    setHasInitializedSelection(false); // reset initialization flag
    onModalClose();
  };

  const saveComps = async () => {
    setIsAddCompsLoading(true);
    try {
      if (data?.body.status !== 'found') {
        // Nothing to save (e.g. still loading or errored) — just close
        dismissModal();
        return;
      }

      const selectedIDs = getSelectedIDs(rowSelection, order.isInspectifyOrder, hasArvOption); // actual comp IDs
      const compsToAdd = selectedIDs.filter((id) => !currentCompsSet[id] && id !== 'subject');
      const rowsWithUADRatings = Object.fromEntries(
        (ref.current?.getRowsWithUADRatings() ?? []).map((row) => [row.id, row]),
      );

      // Build selection state for new comps to add
      const newRowSelection: CompSelectionState = compsToAdd.reduce<CompSelectionState>(
        (acc, id) => {
          const row = rowsWithUADRatings[id];
          acc[id] = {
            ...rowSelection[id],
            uadQualityRating: row?.uadQualityRating,
            uadConditionRating: row?.uadConditionRating,
          };
          return acc;
        },
        {},
      );

      const compsToDelete = Object.keys(currentCompsSet)
        .filter((id) => !selectedIDs.includes(id) && id !== 'subject')
        .map((id) => currentCompsSet[id])
        .filter(isDefined);

      if (compsToDelete.length > 0) {
        dispatch(removeComps({ ids: compsToDelete }));
      }

      if (data.source === 'constellation1' || data.source === 'abode') {
        await handleAddClickForCompCreation({
          isLoading,
          rowSelection: newRowSelection,
          createComps,
          autofillProperty,
          effectiveDate,
          orderID,
          isInspectifyOrder: order.isInspectifyOrder,
          hasArvOption,
        });
      } else if (data.source === 'z') {
        await handleAddClickZillow({
          data: data.body.output.results,
          isLoading,
          rowSelection: newRowSelection,
          createComps,
          autofillProperty,
          effectiveDate,
          orderID,
          isInspectifyOrder: order.isInspectifyOrder,
          hasArvOption,
        });
      }

      dismissModal();
    } catch (e: unknown) {
      console.error(e, 'Failed to add recommended comps.');
      throw e;
    } finally {
      setIsAddCompsLoading(false);
    }
  };

  // Saves the current comp selection then dismisses the modal.
  // Called when the user clicks the X close button.
  const closeModal = async () => saveComps();

  return (
    <FormProvider {...form}>
      <RecCompSelectionProvider
        value={{
          openSimilarityScoreWeightingModal,
          openColumnSettings: onColumnSettingsOpen,
          openQualityTierBreakdown: () => {
            setQualityTierBreakdownData(ref.current?.getQualityTiersBreakdown());
            onQualityTierBreakdownOpen();
          },
          openFilterDrawer: allowRecommendedCompsFiltering ? onFilterDrawerOpen : noop,
        }}
      >
        <Modal
          size="full"
          isOpen={isModalOpen}
          onClose={closeModal}
          closeOnEsc
          showCloseButton
          fullHeight
          modalBodyProps={{ padding: 0, overflow: 'hidden' }}
          title={
            <RecommendedCompsModalHeader
              statistics={statistics}
              isLoading={isLoading}
              onExportCSV={() => ref.current?.downloadCSV()}
            />
          }
        >
          <ColumnSettingsModal isOpen={isColumnSettingsOpen} onClose={onColumnSettingsClose} />

          {SimilarityScoreWeightingModal}
          {allowRecommendedCompsFiltering && (
            <FilterDrawer
              isOpen={isFilterDrawerOpen}
              onCancel={handleFilterDrawerClose}
              onApply={applyFilters}
              subject={subject}
              effectiveDate={effectiveDate}
              isLoading={isLoading}
              statistics={statistics}
              filtersMessage={filtersMessage}
              showSaleDateWarning={allowApplyNewFilters || newFiltersApplied}
              allowApplyNewFilters={allowApplyNewFilters}
              orderID={orderID}
            />
          )}

          <QualityTierBreakdown
            isOpen={isQualityTierBreakdownOpen}
            onClose={onQualityTierBreakdownClose}
            qualityTierBreakdownData={qualityTierBreakdownData}
          />

          <Block position="relative" width="100%" height="100%">
            <LoadingScreenSamFull
              isLoading={isAddCompsLoading || (isLoading && !isFilterDrawerOpen)}
              what="comps"
              wrapperProps={{ backgroundColor: 'surface1', top: '56px', zIndex: 1201 }}
            />
            <Flex height="100%" width="100%">
              <Tkv2ReferenceViewDrawer>
                <RecommendedCompSelection
                  orderID={orderID}
                  subject={subject}
                  comps={currentComps}
                  compSeq={compSeq}
                  effectiveDate={effectiveDate}
                  sorting={sorting}
                  setSorting={setSorting}
                  rowSelection={rowSelection}
                  setRowSelection={handleSetRowSelection}
                  currentComps={currentCompsSet}
                  showNoRecommendations={showNoRecommendations}
                  setShowNoRecommendations={setShowNoRecommendations}
                  getDefaultFormValues={getDefaultFormValues}
                  isInspectifyOrder={order.isInspectifyOrder}
                  hasArvOption={hasArvOption}
                  allowRecommendedCompsFiltering={allowRecommendedCompsFiltering}
                  onClose={closeModal}
                  allowNewSimilarityScore={allowNewSimilarityScore}
                  similarityScoreWeights={similarityScoreWeights}
                  updateCompsLabel="Update comps"
                  onUpdateComps={() => {
                    saveComps().catch(noop);
                  }}
                  onDeselectAll={() => setRowSelection({})}
                  ref={ref}
                />
              </Tkv2ReferenceViewDrawer>
              <Tkv2ReferenceBar />
            </Flex>
          </Block>
        </Modal>
      </RecCompSelectionProvider>
    </FormProvider>
  );
};
